<template>
	<div
		id="journey-case-modal"
		class="modal fade"
		tabindex="1"
		aria-labelledby="journey-case-modal"
		@keyup.esc.stop.prevent="Close()"
	>
		<div
			class="modal-dialog modal-dialog-centered"
			v-if="journeyCase.id"
		>
			<div class="modal-content">
				<GridLayout
					:reactiveSidebar="false"
					:sidebarState="sidebarState"
					class="full p-0"
				>
					<template #left-header>
						<Title value="STATUS" />
					</template>
					<template #left-content>
						<div class="w-full py-3 items-center logs-container">
							<div
								class="w-full log-wrapper"
								v-for="(log, index) in journeyCase.logs"
								:key="`status-${log.id}-${index}`"
							>
								<template v-if="index > 0">
									<div
										class="w-full flex flex-col items-center my-3 log-separator"
									>
										<i
											class="fal fa-chevron-down font-32"
										></i></div
								></template>
								<div class="w-full log-container">
									<p class="mb-0">
										<span>
											<b>{{ log.date }}</b>
										</span>
										<span>
											<b>{{ log.time }}</b>
										</span>
									</p>
									<p class="mb-0">
										<span
											><b>RESPONSÁVEL</b>:
											{{
												log.author?.name?.toUpperCase() ??
												'SISTEMA'
											}}
										</span>
									</p>
									<p class="mb-0">
										<span
											><b>STATUS</b>:
											{{
												log.status_description.toUpperCase()
											}}
										</span>
									</p>
									<p
										class="mb-0"
										v-if="log.observation"
									>
										<span class=""
											><b>OBSERVAÇÕES</b>:
											{{ log.observation.toUpperCase() }}
										</span>
									</p>
								</div>
							</div>
						</div>
					</template>
					<template #right-header>
						<div class="w-full flex justify-between items-center">
							<div class="header-data flex items-center gap-52">
								<div class="flex items-center gap-3">
									<label class="mb-0 text-xl font-bold"
										>NOME:</label
									>
									<span class="text-2xl font-bold">{{
										journeyCase.patient.name
									}}</span>
								</div>
								<div class="flex items-center gap-3">
									<label class="mb-0 text-xl font-bold"
										>TELEFONE:</label
									>
									<span class="text-2xl font-bold">{{
										journeyCase.patient.phone
									}}</span>
								</div>
							</div>
							<div class="close-modal-container">
								<button
									id="close-modal"
									class="btn btn-light btn-sm btn-legacy"
									@click="Close()"
								>
									<i class="fal fa-times"></i>
								</button>
							</div>
						</div>
					</template>
					<template #right-content>
						<ToolBar>
							<div class="flex-1 flex gap-3">
								<div class="flex items-center py-1">
									<button
										class="btn btn-tool"
										:class="{
											'has-filters': hasAtLeastOneLog,
										}"
										@click="ToggleShowSidebar"
									>
										<i class="far fa-list mx-2 font-20"></i>
									</button>
								</div>
								<div class="w-5/12">
									<SearchGroup
										:search="search"
										@change="Change"
										@search="Search"
										@refresh="GetCaseDetails"
									/>
								</div>
								<div
									class="flex-1 flex items-center justify-end"
								>
									<button
										class="btn btn-fixed-size"
										@click="NewScheduling"
										:disabled="isExpired"
									>
										NOVO<br />
										AGENDAMENTO
									</button>
								</div>
							</div>
						</ToolBar>
						<div
							class="w-full h-full flex flex-col items-center main-content"
						>
							<div class="w-full flex-1">
								<Datatables
									class="w-full px-4"
									v-model="journeyCase.schedulings"
									:search="newSearch"
									:headers="headers"
									:checkbox="true"
									:showTotal="false"
									:withHeaderActions="true"
									:fixedHeader="true"
									@checkeds="selectedSchedulings = $event"
								>
									<template #header-actions>
										<th
											v-if="
												journeyCase.schedulings.length
											"
											class="action-column"
										>
											<button
												class="btn btn-danger btn-fixed-size"
												@click="CancelAllSchedulings"
												:disabled="
													!hasAtLeastTwoSelecteds ||
													!hasSomeScheduled ||
													isExpired
												"
											>
												CANCELAR<br />
												AGENDAMENTOS
											</button>
										</th>
									</template>
									<template v-slot:body="{ item }">
										<td class="align-middle">
											{{
												item.proceeding.nome.toUpperCase()
											}}
										</td>
										<td class="align-middle">
											{{ FormatDate(item.data) }}
										</td>
										<td class="align-middle">
											{{
												FormatTime(
													`${item.data} ${item.hora}`
												)
											}}
										</td>
										<td
											class="align-middle"
											:class="`status status-${
												item.attendance.status !==
												SCHEDULING_STATUSES.CANCELLED
													? SCHEDULING_STATUSES.SCHEDULED
													: SCHEDULING_STATUSES.CANCELLED
											}`"
										>
											{{
												item.attendance.status !==
												SCHEDULING_STATUSES.CANCELLED
													? 'AGENDADO'
													: 'CANCELADO [AGD]'
											}}
										</td>
										<td class="align-middle action-column">
											<button
												class="btn btn-danger btn-fixed-size"
												@click="CancelAlert([item.id])"
												:disabled="
													isExpired ||
													item.attendance.status !==
														SCHEDULING_STATUSES.SCHEDULED
												"
											>
												CANCELAR
											</button>
										</td>
									</template>
								</Datatables>
							</div>
							<hr />
							<div
								id="observation-container"
								class="w-full flex flex-col px-4"
							>
								<div class="flex flex-col form-group mb-0">
									<textarea
										name="observation"
										id="observation"
										class="form-control w-full flex-1 overflow-y-auto"
										v-model.trim="observation"
										placeholder="OBSERVAÇÕES:"
									>
									</textarea>
								</div>
							</div>
						</div>
					</template>
					<template #right-footer>
						<div
							class="w-full h-full flex justify-between items-center"
						>
							<button
								class="btn btn-pending btn-fixed-size"
								tabindex="5"
								@click="Pending"
								:disabled="isExpired"
							>
								PENDENTE
							</button>
							<button
								class="btn btn btn-fixed-size"
								tabindex="6"
								@click="NoCallResponse"
								:disabled="isExpired"
							>
								NÃO ATENDIDO
							</button>
							<button
								class="btn btn-success btn-fixed-size"
								tabindex="4"
								@click="Resolve"
								:disabled="isExpired"
							>
								RESOLVIDO
							</button>
						</div>
					</template>
				</GridLayout>
			</div>
		</div>
	</div>
</template>

<script>
	import moment from 'moment/moment'
	import Swal from 'sweetalert2'
	import { mapActions, mapGetters } from 'vuex'

	import Datatables from './Datatables.vue'
	import { useConfirmation, useError, useDeleteAlert } from '@/composables'

	import Heartbeat from '@/utils/heartbeat/heartbeat.js'
	import { _STATUSES } from '../utils/Statuses'
	import { EventBusInstance } from '@/bus'
	import JourneyCasesEvents from '../utils/Events'

	export default {
		name: 'JourneyCaseModal',
		components: {
			Datatables,
		},
		props: {
			journeyId: {
				type: [Number, String],
				required: true,
			},
		},
		data() {
			return {
				search: '',
				newSearch: '',
				sidebarState: false,
				journeyCase: {},
				observation: '',
				headers: [
					{ text: 'MODALIDADE', value: 'proceeding.nome' },
					{ text: 'DATA', value: 'date' },
					{ text: 'HORA', value: 'time' },
					{ text: 'STATUS', value: 'status' },
				],
				selectedSchedulings: [],
				SCHEDULING_STATUSES: {
					SCHEDULED: 1,
					CANCELLED: 13,
				},
			}
		},
		computed: {
			...mapGetters('auth', ['requester_selected_id']),
			hasAtLeastOneLog() {
				return false
			},
			hasAtLeastTwoSelecteds() {
				return this.selectedSchedulings.length > 1
			},
			isExpired() {
				return this.journeyCase.type === 'EXPIRED'
			},
			hasSomeScheduled() {
				return this.journeyCase.schedulings.some(
					s =>
						s.attendance.status ===
						this.SCHEDULING_STATUSES.SCHEDULED
				)
			},
		},
		methods: {
			...mapActions('system', [
				'StartLoading',
				'FinishLoading',
				'RedirectCallback',
			]),
			Open() {
				const modal = window.jQuery('#journey-case-modal')
				modal.modal({
					focus: false,
				})
				modal.modal('show')
			},
			CloseModal() {
				window.jQuery('#journey-case-modal').modal('hide')
				window.jQuery('body').removeClass('modal-open')

				window.jQuery('.modal-backdrop').remove()
				window.Heartbeat.CloseConnection().then(() => {
					window.Heartbeat = null
				})
				this.$emit('close')
			},
			async Close() {
				await this.CloseAttendance()

				this.CloseModal()
			},
			ToggleShowSidebar() {
				this.sidebarState = !this.sidebarState
			},
			Search() {
				this.newSearch = this.search
			},
			async GetCaseDetails() {
				this.StartLoading()

				return window.api
					.call('post', '/journey-cases/get-case-details', {
						journey_id: this.journeyId,
					})
					.then(({ status, data }) => {
						if (status !== 200) {
							Swal.fire({
								icon: 'error',
								title: 'Erro!',
								customClass: {
									container: 'themed',
								},
								text:
									data.message ??
									'Erro ao obter os detalhes do atendimento!',
							}).then(() => this.CloseModal())

							return
						}

						this.journeyCase = data.journey.case
						this.journeyCase.logs = this.journeyCase.logs.map(l => {
							l.date = moment(l.created_at).format('DD/MM/YYYY')
							l.time = moment(l.created_at).format('HH:mm:ss')

							return l
						})
						this.sidebarState = this.journeyCase.logs.length > 0
					})
					.finally(() => this.FinishLoading())
			},
			NewScheduling() {
				const url = new URL(
					`${process.env.VUE_APP_MANAGEMENT_URL}/agenda/pre-agendamento?newSchedulingFlow=true&journey_id=${this.journeyId}`
				)
				const newSchedulingTab = window.open(url, '_blank')

				newSchedulingTab.addEventListener('message', function (event) {
					if (event.type !== 'message') return

					const { data } = event
					if (!data.type || data.type !== 'newScheduling') return

					newSchedulingTab.close()
				})
			},
			CancelAllSchedulings() {
				this.CancelAlert(this.journeyCase.schedulings.map(s => s.id))
			},
			async CancelAlert(schedulingsIds) {
				const { isConfirmed } = await useDeleteAlert({
					title: 'DESEJA MESMO CANCELAR ESTE(S) AGENDAMENTO(S)?',
					html: 'DIGITE "DEL" NO CAMPO ABAIXO',
				})

				if (isConfirmed) {
					this.CancelSchedulings(schedulingsIds)
				}
			},
			async CancelSchedulings(schedulingsIds) {
				this.StartLoading()

				const schedulings = this.journeyCase.schedulings.filter(s =>
					schedulingsIds.includes(s.id)
				)

				return window.api
					.call('post', '/cancel-schedulings', {
						organization_id: this.requester_selected_id,
						schedulings,
					})
					.then(({ status }) => {
						if (status !== 200) {
							Swal.fire({
								icon: 'error',
								title: 'Erro!',
								customClass: {
									container: 'themed',
								},
								text: `Erro ao cancelar o(s) agendamento(s)!`,
							})

							return
						}

						useConfirmation()

						this.GetCaseDetails()
					})
					.finally(() => this.FinishLoading())
			},
			async UpdateJourneyCase({
				status,
				close = true,
				confirmation = true,
				errorCallback = null,
			}) {
				const observation = this.observation.trim()
				return window.api
					.call('post', '/journey-cases/update-case', {
						journey_id: this.journeyCase.id ?? this.journeyId,
						status,
						observation: observation !== '' ? observation : null,
					})
					.then(({ status, data }) => {
						if (status !== 200) {
							useError(
								data.message ??
									`Erro ao atualizar o atendimento para ${data.status_description.toUpperCase()}!`
							).then(() => {
								if (typeof errorCallback === 'function')
									errorCallback()
							})

							return
						}

						if (close) this.Close()
						if (confirmation) useConfirmation()

						this.$emit('refresh')
					})
			},
			async OpenAttendance() {
				this.StartLoading()

				return window.api
					.call('post', '/journey-cases/open-attendance', {
						journey_id: this.journeyCase.id ?? this.journeyId,
					})
					.then(({ status, data }) => {
						if (status !== 200) {
							useError(data.message).then(() => {
								this.Close()
							})

							return
						}

						this.journeyCase = data.journey.case
						this.journeyCase.logs = this.journeyCase.logs.map(l => {
							l.date = moment(l.created_at).format('DD/MM/YYYY')
							l.time = moment(l.created_at).format('HH:mm:ss')

							return l
						})
						this.sidebarState = this.journeyCase.logs.length > 0
					})
					.finally(() => this.FinishLoading())
			},
			Pending() {
				this.UpdateJourneyCase({ status: 'PENDING' })
			},
			NoCallResponse() {
				this.UpdateJourneyCase({ status: 'NO_CALL_RESPONSE' })
			},
			Resolve() {
				this.UpdateJourneyCase({ status: 'RESOLVED' })
			},
			async CloseAttendance() {
				return window.api
					.call('post', '/journey-cases/close-attendance', {
						journey_id: this.journeyCase.id,
					})
					.then(({ status }) => {
						if (status !== 200) {
							useError('Erro ao fechar o atendimento!')

							return
						}

						this.$emit('refresh')
					})
			},
			FormatDate(date) {
				return window.helpers.FormatDate(date)
			},
			FormatTime(datetime) {
				return window.helpers.FormatTime(datetime, 'HH:mm')
			},
			EventBusHandlersInit() {
				EventBusInstance.on(
					[...JourneyCasesEvents],
					this.JourneyCasesGateway
				)
			},
			EventBusHandlersDestroy() {
				EventBusInstance.off(
					[...JourneyCasesEvents],
					this.JourneyCasesGateway
				)
			},
			JourneyCasesGateway(eventData) {
				switch (eventData.eventType) {
					case '.redirect-case-attendance':
						this.RedirectCaseAttendanceHandler(eventData)
						break
				}
			},
			RedirectCaseAttendanceHandler(eventData) {
				const isSameCase = this.journeyId === eventData.journey_id
				if (!isSameCase) return

				if (!window.Session) return
				const isSameSession =
					window.Session.tab_id === eventData.session_id
				if (!isSameSession) return

				this.$emit('refresh')

				this.CloseModal()
			},
			async CreateHeartbeatInstance() {
				try {
					await this.$nextTick()
					window.Heartbeat = new Heartbeat()
				} catch (error) {
					console.error(
						'Error on running CreateHeartbeatInstance()',
						error
					)
					setTimeout(() => {
						this.CreateHeartbeatInstance()
					}, 500)
				}
			},
			Change(newSearch) {
				this.search = newSearch
			},
		},
		async beforeMount() {
			await this.OpenAttendance()
		},
		async mounted() {
			this.Open()

			window.jQuery('#journey-case-modal').on('hidden.bs.modal', () => {
				window.jQuery('body').removeClass('modal-open')

				window.jQuery('.modal-backdrop').remove()
				window.Heartbeat.CloseConnection().then(() => {
					window.Heartbeat = null
				})
				this.$emit('close')
			})

			await this.$nextTick()

			setTimeout(() => {
				document.querySelector('textarea#observation')?.focus()
			}, 300)

			this.CreateHeartbeatInstance()

			this.EventBusHandlersInit()
		},
		beforeUnmount() {
			this.EventBusHandlersDestroy()
		},
	}
</script>

<style lang="scss" scoped>
	#journey-case-modal {
		.modal-dialog {
			max-width: 100vw;
			max-height: 100vh;
			margin: 0;

			.modal-content {
				height: 100vh;
				display: flex;
				flex-direction: column;
				align-items: center;
			}
		}

		:deep(.grid-layout #left-col) {
			grid-template-rows: var(--secondaryQuadrantHeight) 1fr;
			grid-template-areas:
				'left-header'
				'left-content';
		}

		:deep(.grid-layout #left-col #left-footer) {
			display: none;
		}

		.header-data {
			label {
				color: var(--color-font);
			}

			span {
				color: var(--fontColor);
			}
		}

		textarea#observation {
			font-size: 1.5rem;
			text-transform: uppercase;
			resize: none;

			&::placeholder {
				font-weight: bold;
			}
		}

		.logs-container {
			padding-right: 0.75rem;
			display: flex;
			flex-direction: column;
			align-items: center;

			.log-wrapper {
				color: var(--fontColor);

				.log-separator {
					i {
						color: #6d6d6d;
					}
				}

				.log-container {
					padding: 1rem 0.8rem;
					border: 1px solid var(--hr);
					border-radius: 0.5rem;
					display: flex;
					flex-direction: column;

					p {
						display: flex;
						align-items: center;
						gap: 1rem;
						font-size: 1.1rem;

						label {
							margin: 0;
						}
					}
				}
			}
		}

		.btn-small {
			width: 9rem;
			height: 2.5rem;
			min-height: unset;
			max-height: unset;
			padding: 0.25rem 1rem;
			font-size: 0.8rem;
		}

		.status {
			font-weight: bold;
		}

		.status-1 {
			color: var(--resolved);
		}
		.status-13 {
			color: var(--suspended);
		}
	}
</style>
